import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const FIELDS = [
  ['trading_name', 'Trading / display name'], ['legal_name', 'Legal entity name *'], ['abn', 'ABN *'], ['acn', 'ACN'],
  ['ndis_provider_number', 'NDIS provider number *'], ['ndis_registration_status', 'NDIS registration status'],
  ['contact_phone', 'Phone'], ['contact_email', 'Email'], ['website', 'Website'], ['after_hours_contact', 'After-hours / on-call contact'],
];
const ADDR = [['line1', 'Street address'], ['suburb', 'Suburb'], ['state', 'State'], ['postcode', 'Postcode']];
const REQUIRED_LABEL = { legal_name: 'Legal entity name', abn: 'ABN', ndis_provider_number: 'NDIS provider number' };
const errMsg = (e, fb) => e?.response?.data?.detail?.message || e?.response?.data?.detail?.error?.message || e?.response?.data?.error?.message || fb;

/** Day 24F-C — canonical provider/legal identity (organisations record via /v2/organisations). Never writes org_settings/ORG. */
export const ProviderIdentityCard = () => {
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/v2/organisations/current'); const d = r.data?.data || {};
      setOrg(d);
      setForm({ ...Object.fromEntries(FIELDS.map(([k]) => [k, d[k] || ''])), address: typeof d.address === 'object' && d.address ? d.address : { line1: typeof d.address === 'string' ? d.address : '' } });
    } catch (e) { toast.error(errMsg(e, 'Could not load provider identity')); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([k, v]) => k !== 'address' && v !== '' && v !== (org?.[k] || '')));
      if (JSON.stringify(form.address) !== JSON.stringify(org?.address || {})) body.address = form.address;
      if (Object.keys(body).length === 0) { toast.message('No changes to save'); return; }
      await api.put(`/v2/organisations/${org.id}`, body); toast.success('Provider identity saved'); await load();
    } catch (e) { toast.error(errMsg(e, 'Could not save provider identity')); } finally { setSaving(false); }
  };
  if (!org) return null;
  const missing = org.identity_incomplete_fields || ['legal_name', 'abn', 'ndis_provider_number'].filter((k) => !org[k]);
  return (
    <Card data-testid="provider-identity-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Provider identity (legal)</CardTitle>
        <CardDescription>These details are printed on every generated Service Agreement. They are the canonical organisation record — not the operational settings above.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {missing.length > 0
          ? <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm flex gap-2" data-testid="provider-identity-incomplete"><AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" /><div><strong>Provider identity incomplete.</strong> Missing: {missing.map((k) => REQUIRED_LABEL[k] || k).join(', ')}. Agreements will print blank provider details until these verified values are entered.</div></div>
          : <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm flex gap-2" data-testid="provider-identity-complete"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /><div><strong>Provider identity complete.</strong></div></div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, label]) => <div className="space-y-1" key={k}><Label>{label}</Label><Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`provider-identity-${k}`} /></div>)}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          {ADDR.map(([k, label]) => <div className="space-y-1" key={k}><Label>{label}</Label><Input value={form.address?.[k] || ''} onChange={(e) => setForm({ ...form, address: { ...form.address, [k]: e.target.value } })} data-testid={`provider-identity-address-${k}`} /></div>)}
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving} data-testid="provider-identity-save">{saving ? 'Saving…' : 'Save provider identity'}</Button>
          <Badge variant="outline" className="text-[11px]">* required for complete identity</Badge>
        </div>
      </CardContent>
    </Card>
  );
};
